import crypto from 'crypto';
import fs from 'fs';
import path from 'path';
import zlib from 'zlib';

// Record/replay layer for the Contentful clients, so Happo builds can render a
// content snapshot instead of whatever the CMS serves during the run.
//
// Only `getEntries`/`getEntry` are wrapped — the repo's entire call surface.
// The snapshot is gzip of
// `{$store, responses}`: `responses` maps a stable hash of (space, method,
// args) to a response skeleton, and each Entry/Asset subtree lives once in
// `$store` with occurrences replaced by `{"$ref": <hash>}` — deduped because
// `withoutUnresolvableLinks` embeds shared entries into every response
// (~300 MB raw, ~9 MB deduped + gzipped). scripts/merge-cms-snapshot.js
// builds and verifies the file; replay rehydrates refs on lookup. Record
// mode scatters raw per-key files into happo-io/cms-record/ (parallel Next
// workers can't share one file) for the merge to fold.
//
// When the snapshot has no entry for a request, replay fetches it live rather
// than failing the build. Keys the snapshot does hold still always replay, so
// recorded pages stay pinned; a key that was never recorded has nothing to
// drift from anyway. Failing on one would block every PR that changes a CMS
// query, since only master ever gets recorded: the new query cannot reach the
// snapshot until the PR merges, and the PR cannot merge while its build dies.
//
// Circular link chains (entry A embeds B embeds A) are recorded via the SDK's
// own `stringifySafe`, which snips the cycle into a plain link object. Master
// and PR builds both replay the same snipped shape, so comparisons stay
// internally consistent; production builds never set these flags and are
// untouched.

const RECORD_DIR = path.resolve(
  process.env.CMS_RECORD_DIR || path.join('happo-io', 'cms-record'),
);

const snapshotFile = () => {
  return path.resolve(process.env.CMS_SNAPSHOT_FILE);
};
const replayEnabled = () => {
  return Boolean(process.env.CMS_SNAPSHOT_FILE);
};
const recordEnabled = () => {
  return process.env.HAPPO_PAGES_FIXTURES_RECORD === 'true';
};
// JSON.stringify with sorted object keys, so call sites that build the same
// query with different key order share one fixture entry.
const stableStringify = (value) => {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    const keys = Object.keys(value).sort();
    return `{${keys
      .map((key) => {
        return `${JSON.stringify(key)}:${stableStringify(value[key])}`;
      })
      .join(',')}}`;
  }
  return JSON.stringify(value);
};

const requestKey = (space, method, args) => {
  return crypto
    .createHash('sha256')
    .update(stableStringify({ space, method, args }))
    .digest('hex');
};

const serializeResponse = (response) => {
  if (response && typeof response.stringifySafe === 'function') {
    return response.stringifySafe();
  }
  return JSON.stringify(response);
};

// Loaded once per process: `{$store, responses}` as written by
// scripts/merge-cms-snapshot.js.
let snapshot = null;
const loadSnapshot = () => {
  if (snapshot === null) {
    snapshot = JSON.parse(
      zlib.gunzipSync(fs.readFileSync(snapshotFile())).toString('utf8'),
    );
  }
  return snapshot;
};

// Rehydrates `{"$ref": <hash>}` markers from `$store`. Builds fresh objects on
// every call so one page mutating a shared entry cannot leak into another.
const expand = (value, store) => {
  if (Array.isArray(value)) {
    return value.map((item) => {
      return expand(item, store);
    });
  }
  if (value && typeof value === 'object') {
    if (typeof value.$ref === 'string' && Object.keys(value).length === 1) {
      return expand(store[value.$ref], store);
    }
    const out = {};
    for (const key of Object.keys(value)) {
      out[key] = expand(value[key], store);
    }
    return out;
  }
  return value;
};

const missedKeys = new Set();
const MISS_LOG_LIMIT = 20;

const reportMiss = (key, description) => {
  if (missedKeys.has(key)) {
    return;
  }
  if (missedKeys.size === 0) {
    process.on('exit', () => {
      // eslint-disable-next-line no-console
      console.warn(
        `[cms-fixture] ${missedKeys.size} request(s) in this worker were not ` +
          'covered by the CMS snapshot and were fetched live. Those ' +
          'pages render unpinned content until the next scheduled refresh ' +
          'records them.',
      );
    });
  }
  missedKeys.add(key);
  if (missedKeys.size > MISS_LOG_LIMIT) {
    return;
  }
  // eslint-disable-next-line no-console
  console.warn(
    `[cms-fixture] snapshot miss, fetching live: ${description}${
      missedKeys.size === MISS_LOG_LIMIT
        ? ' (further misses in this worker are counted, not listed)'
        : ''
    }`,
  );
};

const wrapMethod = (client, space, method) => {
  return async (...args) => {
    const key = requestKey(space, method, args);
    if (!recordEnabled()) {
      const { $store, responses } = loadSnapshot();
      const skeleton = responses[key];
      if (skeleton === undefined) {
        const description = `${space}.${method}(${stableStringify(args)})`;
        reportMiss(key, description);
        return client[method](...args);
      }
      return expand(skeleton, $store);
    }
    const response = await client[method](...args);
    fs.mkdirSync(RECORD_DIR, { recursive: true });
    fs.writeFileSync(
      path.join(RECORD_DIR, `${key}.json`),
      serializeResponse(response),
    );
    return response;
  };
};

let warnedMissingSnapshot = false;

/**
 * Wraps a Contentful client with the record/replay fixture layer. A no-op
 * unless HAPPO_PAGES_FIXTURES_RECORD or CMS_SNAPSHOT_FILE is set, so
 * production builds are untouched.
 *
 * Replay with no snapshot at all falls through to live fetches with a warning.
 * Once a snapshot exists, an individual missing key falls through the same
 * way — see the miss policy at the top of this file.
 */
export const withCmsFixture = (client, space) => {
  if (!replayEnabled() && !recordEnabled()) {
    return client;
  }
  if (!recordEnabled() && !fs.existsSync(snapshotFile())) {
    if (!warnedMissingSnapshot) {
      warnedMissingSnapshot = true;
      // eslint-disable-next-line no-console
      console.warn(
        `[cms-fixture] ${snapshotFile()} does not exist. Falling back to live Contentful fetches.`,
      );
    }
    return client;
  }
  return {
    ...client,
    getEntries: wrapMethod(client, space, 'getEntries'),
    getEntry: wrapMethod(client, space, 'getEntry'),
  };
};
